import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import html2canvas from 'html2canvas';
import { useNotifications } from './Notifications';

const MapComponent = ({ onImageGeneration, isGenerating, onBack }) => {
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const [map, setMap] = useState(null);
  const [arrow, setArrow] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isWaitingForSecondClick, setIsWaitingForSecondClick] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [tempLine, setTempLine] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const tempLineRef = useRef(null);
  const { notifyWarning, notifyError } = useNotifications();

  useEffect(() => {
    initializeMap();
    
    // Cleanup function
    return () => {
      clearTempLine();
      if (tempLineRef.current) {
        tempLineRef.current.setMap(null);
        tempLineRef.current = null;
      }
    };
    // mount-only on purpose: adding deps here would tear down and rebuild the map
    // Verifying a change here needs a live Google Map and a Maps API key,
    // which CI cannot exercise. Revisit with manual testing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up autocomplete after map is loaded
  useEffect(() => {
    if (map && window.google && searchInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
        fields: ['place_id', 'geometry', 'name', 'formatted_address'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          map.setCenter(place.geometry.location);
          map.setZoom(15);
          console.log('Map moved to:', place.formatted_address);
          setSearchQuery(''); // Clear search after selection
        }
      });
    }
  }, [map]);

  // Add event listeners when isDrawing changes
  useEffect(() => {
    if (map && window.google) {
      setupEventListeners();
    }
    // re-registers map listeners only when the drawing state actually changes
    // Verifying a change here needs a live Google Map and a Maps API key,
    // which CI cannot exercise. Revisit with manual testing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing, map, isWaitingForSecondClick, startPoint]);

  useEffect(() => {
    if (map) {
      if (isDrawing) {
        // Disable map dragging when in drawing mode
        map.setOptions({ draggable: false });
      } else {
        // Enable map dragging when not in drawing mode
        map.setOptions({ draggable: true });
        // Clean up any remaining temp lines when exiting drawing mode
        if (!isDrawing) {
          clearTempLine();
          tempLineRef.current = null;
        }
      }
    }
    // clearTempLine only touches refs and the map instance
    // Verifying a change here needs a live Google Map and a Maps API key,
    // which CI cannot exercise. Revisit with manual testing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing, map]);

  const initializeMap = async () => {
    // Use the original Maps API key for loading the map
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY',
      version: 'weekly',
      libraries: ['geometry', 'places'],
    });

    try {
      const google = await loader.load();
      const mapInstance = new google.maps.Map(mapRef.current, {
        center: { lat: 40.7128, lng: -74.0060 },
        zoom: 15,
      });

      setMap(mapInstance);
      window.google = google; // Store google object for later use
    } catch (error) {
      console.error('Error loading Google Maps:', error);
    }
  };

  const setupEventListeners = () => {
    if (!map || !window.google) return;

    // Clear existing listeners to avoid duplicates
    window.google.maps.event.clearListeners(map, 'mousedown');
    window.google.maps.event.clearListeners(map, 'mousemove');
    window.google.maps.event.clearListeners(map, 'mouseup');
    window.google.maps.event.clearListeners(map, 'click');

    if (isDrawing) {
      console.log('Setting up drawing event listeners');
      
      // Click event for two-click arrow drawing
      map.addListener('click', (event) => {
        console.log('Map clicked in drawing mode, waiting for second click:', isWaitingForSecondClick);
        
        if (!isWaitingForSecondClick) {
          // First click - set start point
          console.log('First click - setting start point');
          setStartPoint(event.latLng);
          setIsWaitingForSecondClick(true);
          
          // Clear existing arrow
          if (arrow) {
            arrow.setMap(null);
            setArrow(null);
          }
        } else {
          // Second click - create arrow and finish
          console.log('Second click - creating arrow');
          drawArrow(startPoint, event.latLng, map, window.google);
          
          // Clean up and exit drawing mode
          clearTempLine();
          tempLineRef.current = null;
          setStartPoint(null);
          setIsWaitingForSecondClick(false);
          setIsDrawing(false);
        }
      });

      // Mouse move event to show temporary line
      map.addListener('mousemove', (event) => {
        if (isWaitingForSecondClick && startPoint) {
          // Clear previous temp line immediately
          if (tempLineRef.current) {
            tempLineRef.current.setMap(null);
          }
          
          // Create new temporary line with lower z-index so it doesn't block clicks
          const line = new window.google.maps.Polyline({
            path: [startPoint, event.latLng],
            geodesic: false,
            strokeColor: '#FF0000',
            strokeOpacity: 0.5,
            strokeWeight: 2,
            map: map,
            clickable: false,  // Make it non-clickable so it doesn't interfere
            zIndex: 1  // Lower z-index
          });
          
          tempLineRef.current = line;
          setTempLine(line);
        }
      });
    }
  };

  const clearTempLine = () => {
    if (tempLineRef.current) {
      tempLineRef.current.setMap(null);
      tempLineRef.current = null;
    }
    if (tempLine) {
      tempLine.setMap(null);
      setTempLine(null);
    }
  };

  const drawArrow = (startPos, endPos, mapInstance, google) => {
    console.log('Drawing arrow from', startPos.toJSON(), 'to', endPos.toJSON());
    
    // Calculate the angle from start to end point
    const angle = google.maps.geometry.spherical.computeHeading(startPos, endPos);
    
    // Create the arrow line
    const arrowLine = new google.maps.Polyline({
      path: [startPos, endPos],
      geodesic: false,
      strokeColor: '#FF0000',
      strokeOpacity: 1.0,
      strokeWeight: 4,
      map: mapInstance
    });

    // Create arrow head at the end point
    const arrowHead = new google.maps.Marker({
      position: endPos,
      map: mapInstance,
      icon: {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 8,
        fillColor: 'red',
        fillOpacity: 1,
        strokeColor: 'darkred',
        strokeWeight: 2,
        rotation: angle
      },
      draggable: true,
    });

    // Create start point marker
    const startMarker = new google.maps.Marker({
      position: startPos,
      map: mapInstance,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 6,
        fillColor: 'red',
        fillOpacity: 1,
        strokeColor: 'darkred',
        strokeWeight: 2,
      },
      draggable: true,
    });

    // Update line when markers are dragged
    const updateArrow = () => {
      const newStartPos = startMarker.getPosition();
      const newEndPos = arrowHead.getPosition();
      const newAngle = google.maps.geometry.spherical.computeHeading(newStartPos, newEndPos);
      
      arrowLine.setPath([newStartPos, newEndPos]);
      arrowHead.setIcon({
        ...arrowHead.getIcon(),
        rotation: newAngle
      });
    };

    startMarker.addListener('dragend', updateArrow);
    arrowHead.addListener('dragend', updateArrow);

    // Store arrow components
    const arrowComponents = {
      line: arrowLine,
      head: arrowHead,
      start: startMarker,
      setMap: (map) => {
        arrowLine.setMap(map);
        arrowHead.setMap(map);
        startMarker.setMap(map);
      }
    };

    setArrow(arrowComponents);
    setIsDrawing(false);
  };

  const handleDrawArrow = () => {
    setIsDrawing(true);
    setIsWaitingForSecondClick(false);
    setStartPoint(null);
    clearTempLine();
    // Also clear the ref
    tempLineRef.current = null;
  };

  const handleClearArrow = () => {
    if (arrow) {
      arrow.setMap(null);
      setArrow(null);
    }
    clearTempLine();
    tempLineRef.current = null;
    setIsDrawing(false);
    setIsWaitingForSecondClick(false);
    setStartPoint(null);
  };

  const handleGenerateImage = async () => {
    // Guard as well as disable the button: a double-click is what triggers the credit race.
    if (isGenerating) return;

    if (!arrow) {
      notifyWarning('Draw an arrow on the map first, then generate.');
      return;
    }

    try {
      const canvas = await html2canvas(mapRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
      });

      // Get arrow location data
      const startPosition = arrow.start.getPosition();
      const arrowLocation = {
        lat: startPosition.lat(),
        lng: startPosition.lng()
      };

      canvas.toBlob((blob) => {
        onImageGeneration(blob, arrowLocation);
      }, 'image/png');
    } catch (error) {
      console.error('Error capturing map:', error);
      notifyError('Could not capture the map. Please try again.');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !window.google) return;

    const service = new window.google.maps.places.PlacesService(map);
    const request = {
      query: searchQuery,
      fields: ['place_id', 'geometry', 'name', 'formatted_address'],
    };

    service.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results[0]) {
        const place = results[0];
        if (place.geometry && place.geometry.location) {
          map.setCenter(place.geometry.location);
          map.setZoom(15);
          console.log('Map moved to:', place.formatted_address);
          setSearchQuery(''); // Clear search after successful search
        }
      } else {
        notifyWarning('We could not find that location. Try a different search term.');
      }
    });
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div>
      <div className="feature-header">
        <button className="btn btn-secondary back-btn" onClick={onBack}>
          <ArrowLeftIcon style={{ marginRight: '8px' }} />
          Back to Dashboard
        </button>
        <div className="header-content">
          <div className="feature-icon">
            <ArrowRightIcon style={{ width: '32px', height: '32px' }} />
          </div>
          <div>
            <h1>Arrow Vision</h1>
            <p>Draw arrows on maps and see AI-generated perspective views</p>
          </div>
        </div>
      </div>
      
      <div className="map-search">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search for a city or address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          className="search-input"
          disabled={isGenerating}
        />
        <button 
          onClick={handleSearch}
          disabled={isGenerating || !searchQuery.trim()}
          className="btn btn-secondary search-btn"
        >
          Search
        </button>
      </div>
      
      <div className="map-controls">
        <button 
          className={`btn ${isDrawing ? 'btn-danger' : 'btn-primary'}`}
          onClick={handleDrawArrow}
          disabled={isGenerating}
        >
          {isDrawing ? (isWaitingForSecondClick ? 'Click to finish arrow' : 'Click to start arrow') : 'Draw Arrow'}
        </button>
        
        <button 
          className="btn btn-danger"
          onClick={handleClearArrow}
          disabled={isGenerating || !arrow}
        >
          Clear Arrow
        </button>
        
        <button 
          className="btn btn-primary"
          onClick={handleGenerateImage}
          disabled={isGenerating || !arrow}
        >
          {isGenerating ? 'Generating...' : 'Generate Image'}
        </button>
      </div>
      
      <div className="map-wrapper">
        <div
          ref={mapRef}
          className="map-container"
        />
        {isGenerating && (
          <div className="map-busy-overlay">
            <span className="spinner" />
            <span>Generating - the map is locked until this finishes</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapComponent;